<template>
  <a
    v-if="isAndroid"
    :href="downloadUrl"
    target="_blank"
    class="btns"
    @click.prevent="registerDocExport"
  >
    Download PDF
  </a>
  <a
    v-else
    :href="downloadUrl"
    target="_blank"
    class="btns"
    :download="downloadName"
    @click="registerDocExport"
  >
    <span v-if="!showSpinner">Download PDF</span>
    <span v-else>Downloading<button-spinner /></span>
  </a>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'

import { platform } from '@/services/platform'
import { apiURL } from '@/services/request'
import { BookView } from '@/models/doc.book'
import { assertNotNull } from '@/helpers/typing'

const props = defineProps<{
  book: BookView
}>()

const isAndroid = ref(false)
const showSpinner = ref(false)

const spinnerDelay = ref(4000)

onBeforeMount(async () => {
  // The `download` link property does not work on Android (just nothing happens
  // when clicking the link, no error and no download).
  // So we are rendering the link without that property.
  // In other cases, the property is usefl and browsers handle the file download
  // in a better way.
  //
  // Note: the problem with the `download` property is not generic, it is also related
  // to the app url, as download worked for me when I set app URL to local,
  // like http://192.168.88.254:8080/app and the pdf URL starts with
  // http://192.168.88.254:5000/api in this case.
  // While on dev URLs are https://dev.shortform.com/app and https://dev.shortform.com/api
  // and the download does not work.
  isAndroid.value = await platform.isNativeAndroidApp()
})

async function registerDocExport(): Promise<void> {
  props.book.registerDocExport()

  if (isAndroid.value) {
    // On Android, the link from the `<a>` tag doesn't open
    // in the browser, so we have to open it manually.
    await platform.openUrl(downloadUrl.value)
  }

  showSpinner.value = true
  setTimeout(() => (showSpinner.value = false), spinnerDelay.value)
}

const downloadName = computed(() => {
  // We replace `/` with `-` in the file name as the server interprets the `/` as a URL
  // path separator and we end up with a 404 error.
  //
  // We can safely do the replacement here: the title can actually be anything and is used
  // for a local file name when the browser saves the PDF.
  // Our backend uses the `token` parameter in the download URL to identify the file.
  const downloadTitle = props.book.title.replace('/', '-')
  return `Shortform Summary - ${downloadTitle}.pdf`
})

const downloadUrl = computed(() => {
  const token = assertNotNull(props.book.doc_export_stats).token
  const title = encodeURIComponent(downloadName.value)
  return apiURL + `pdfbooks/${title}?token=${token}`
})
</script>
